import mixpanel from "mixpanel-browser";
import type { AnalyticsDestination, EventProperties } from "@/lib/analytics/analytics.types";
import { isSentinelValue } from "@/lib/env";

const TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

let initialized = false;

const enabled = (): boolean =>
  typeof window !== "undefined" && !!TOKEN && !isSentinelValue(TOKEN) && process.env.NODE_ENV !== "test";

const ensureInit = (): void => {
  if (initialized || !enabled() || !TOKEN) {
    return;
  }
  mixpanel.init(TOKEN, {
    track_pageview: false,
    persistence: "localStorage",
    ignore_dnt: false,
  });
  initialized = true;
};

const toMixpanelProperties = (properties?: EventProperties): Record<string, string | number | boolean> | undefined => {
  if (!properties) {
    return undefined;
  }
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      out[key] = value;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

export const mixpanelDestination: AnalyticsDestination = {
  name: "mixpanel",
  isEnabled: enabled,
  pageView: (path) => {
    ensureInit();
    if (!enabled()) {
      return;
    }
    mixpanel.track("$pageview", { path });
  },
  track: (name, properties) => {
    ensureInit();
    if (!enabled()) {
      return;
    }
    mixpanel.track(name, toMixpanelProperties(properties));
  },
  identify: (traits) => {
    ensureInit();
    if (!enabled()) {
      return;
    }
    if (traits.id) {
      mixpanel.identify(traits.id);
    }
    const { id: _id, email, ...rest } = traits;
    const peopleProps: Record<string, string | number | boolean> = {};
    if (email) {
      peopleProps.$email = email;
    }
    for (const [key, value] of Object.entries(rest)) {
      if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
        peopleProps[key] = value;
      }
    }
    if (Object.keys(peopleProps).length > 0) {
      mixpanel.people.set(peopleProps);
    }
  },
  reset: () => {
    if (!enabled()) {
      return;
    }
    ensureInit();
    mixpanel.reset();
  },
};
